import { AxiosResponse } from 'axios';

import {
  searchInviteLocationsRequest,
  searchInvitePaySchedulesRequest,
  searchInviteTeamsRequest,
} from '@vyce/core/src/api/invites';
import { PaginatedList } from '@vyce/core/src/api/types';
import { PaySchedule, Team, TimeAndAttendanceLocation } from '@vyce/core/src/types';
import { siteConverter } from '@vyce/core/src/utils/location';

export interface InviteResources {
  teams: Team[];
  /** Locations carry their shifts (locations[].shifts) — the Time targets read them from here */
  locations: TimeAndAttendanceLocation[];
  paySchedules: PaySchedule[];
}

export type FetchInviteResources = (companyId: string) => Promise<InviteResources>;

const PAGE_SIZE = 200;

// One failing inventory must not blank the other two — log it and carry on with an empty list
const orEmpty = async <T>(request: () => Promise<T[]>): Promise<T[]> => {
  try {
    return await request();
  } catch (e) {
    console.error(e);
    return [];
  }
};

// Every target must be on offer, so read pages until `count` is reached. The offset advances by
// what actually came back, so a server-side cap on `limit` cannot skip anything.
const fetchAllPages = async <T>(
  request: (offset: number, limit: number) => Promise<AxiosResponse<PaginatedList<T>>>
): Promise<T[]> => {
  const items: T[] = [];
  for (;;) {
    const { data } = await request(items.length, PAGE_SIZE);
    const page = data?.items ?? [];
    items.push(...page);
    if (!page.length || items.length >= (data?.count ?? 0)) {
      return items;
    }
  }
};

/**
 * Default loader for the employer app: the invite-scoped routes return every team / location /
 * pay schedule of the company, whatever the caller's rights, so anyone can be invited anywhere.
 */
export const fetchInviteResources: FetchInviteResources = async companyId => {
  const [teams, locations, paySchedules] = await Promise.all([
    orEmpty(async () => {
      const items = await fetchAllPages((offset, limit) =>
        searchInviteTeamsRequest(companyId, { offset, limit, order_by: [] })
      );
      // The route's sort keys are not documented — order by name here
      return items.sort((a, b) => a.name.localeCompare(b.name));
    }),
    orEmpty(async () => {
      const items = await fetchAllPages((offset, limit) =>
        searchInviteLocationsRequest(companyId, { offset, limit, show_deleted: false, order_by: [] })
      );
      return siteConverter(items).locations;
    }),
    orEmpty(() =>
      fetchAllPages((offset, limit) =>
        searchInvitePaySchedulesRequest(companyId, {
          offset,
          limit,
          order_by: [{ field_name: 'name', desc: false }],
        })
      )
    ),
  ]);

  return { teams, locations, paySchedules };
};
